import React, { useState } from "react";
import { authenticatedPost } from "../utils/apiUtils";

const CreateAsset = ({ history }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assetType, setAssetType] = useState("");
  const [properties, setProperties] = useState([{ name: "", value: "" }]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [pendingAsset, setPendingAsset] = useState(null);
  const [requiresAuthentication, setRequiresAuthentication] = useState(false);

  const predefinedTypes = [
    "Vehicle",
    "Appliance",
    "Real Estate Property",
    "Furniture",
    "Electronics",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newAsset = {
      name,
      description,
      assetType,
      properties,
    };

    try {
      const response = await authenticatedPost("/assets", newAsset);

      if (response.ok) {
        setSuccess("Item registered successfully!");
        setName("");
        setDescription("");
        setAssetType("");
        setProperties([{ name: "", value: "" }]);
        setError("");
        setRequiresAuthentication(false);
        setShowPasswordModal(false);
        setPendingAsset(null);
        // Redirect to home or another page after a short delay
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      } else {
        const errorData = await response.json();
        if (errorData.requiresAuthentication) {
          // Session authentication is required - show password modal
          setPendingAsset(newAsset);
          setRequiresAuthentication(true);
          setShowPasswordModal(true);
          setError("");
        } else {
          throw new Error(errorData.message || "Failed to create item");
        }
      }
    } catch (err) {
      setError("Failed to register item. Please try again.");
      setSuccess("");
      setRequiresAuthentication(false);
      setShowPasswordModal(false);
    }
  };

  const authenticateAndRetry = async () => {
    if (!password.trim()) {
      setError("Please enter your encryption password.");
      return;
    }

    if (!pendingAsset) {
      setError("No pending asset to create.");
      return;
    }

    try {
      // First authenticate the session with the password
      const authResponse = await authenticatedPost("/encryption/authenticate-session", {
        password: password,
        rememberSession: false
      });

      if (authResponse.ok) {
        // Authentication successful, now retry creating the asset
        const response = await authenticatedPost("/assets", pendingAsset);

        if (response.ok) {
          setSuccess("Item registered successfully!");
          setName("");
          setDescription("");
          setAssetType("");
          setProperties([{ name: "", value: "" }]);
          setError("");
          setRequiresAuthentication(false);
          setShowPasswordModal(false);
          setPendingAsset(null);
          setPassword("");
          // Redirect to home or another page after a short delay
          setTimeout(() => {
            window.location.href = "/";
          }, 2000);
        } else {
          const errorData = await response.json();
          setError(
            errorData.message || "Failed to create item after authentication."
          );
        }
      } else {
        const errorData = await authResponse.json();
        setError(
          errorData.message ||
            "Authentication failed. Please check your password."
        );
      }
    } catch (err) {
      setError("Authentication failed. Please try again.");
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPassword("");
    setPendingAsset(null);
    setRequiresAuthentication(false);
  };

  const handlePropertyChange = (index, event) => {
    const updatedProperties = [...properties];
    updatedProperties[index][event.target.name] = event.target.value;
    setProperties(updatedProperties);
  };

  const addProperty = () => {
    setProperties([...properties, { name: "", value: "" }]);
  };

  const removeProperty = (index) => {
    const updatedProperties = [...properties];
    updatedProperties.splice(index, 1);
    setProperties(updatedProperties);
  };

  return (
    <div className="create-item-container">
      <div className="create-item-header">
        <h1 className="create-item-title">Register New Item</h1>
        <p className="create-item-subtitle">
          Add a new item to your registry with all the important details
        </p>
      </div>

      <div className="create-item-form-card">
        <form onSubmit={handleSubmit} className="create-item-form">
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>

            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Item Name *
              </label>
              <input
                type="text"
                className="form-control modern-input"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter the name of your item"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Item Description *
              </label>
              <textarea
                className="form-control modern-textarea"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your item in detail"
                rows="4"
                required
              ></textarea>
            </div>

            <div className="form-group">
              <label htmlFor="assetType" className="form-label">
                Item Type *
              </label>
              <input
                list="assetTypeOptions"
                className="form-control modern-input"
                id="assetType"
                value={assetType}
                onChange={(e) => setAssetType(e.target.value)}
                placeholder="Select or enter item type"
                required
              />
              <datalist id="assetTypeOptions">
                {predefinedTypes.map((type, index) => (
                  <option key={index} value={type} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3 className="section-title">Item Details</h3>
              <p className="section-description">
                Add specific details about your item
              </p>
            </div>

            <div className="details-container">
              {properties.map((property, index) => (
                <div key={index} className="detail-item-card">
                  <div className="detail-item-header">
                    <span className="detail-number">Detail #{index + 1}</span>
                    {properties.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm remove-detail-btn"
                        onClick={() => removeProperty(index)}
                        title="Remove this detail"
                      >
                        <span className="btn-icon">×</span>
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="detail-inputs">
                    <div className="form-group">
                      <label
                        htmlFor={`property-name-${index}`}
                        className="form-label"
                      >
                        Detail Name *
                      </label>
                      <input
                        type="text"
                        className="form-control modern-input"
                        id={`property-name-${index}`}
                        name="name"
                        value={property.name}
                        onChange={(e) => handlePropertyChange(index, e)}
                        placeholder="e.g., Color, Brand, Serial Number"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label
                        htmlFor={`property-value-${index}`}
                        className="form-label"
                      >
                        Detail Value *
                      </label>
                      <input
                        type="text"
                        className="form-control modern-input"
                        id={`property-value-${index}`}
                        name="value"
                        value={property.value}
                        onChange={(e) => handlePropertyChange(index, e)}
                        placeholder="Enter the value"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="btn btn-outline-primary add-detail-btn"
              onClick={addProperty}
            >
              <span className="btn-icon">+</span>
              Add Another Detail
            </button>
          </div>

          {error && (
            <div className="alert alert-danger modern-alert">
              <span className="alert-icon">⚠️</span>
              {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success modern-alert">
              <span className="alert-icon">✅</span>
              {success}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-lg submit-btn">
              <span className="btn-icon">📝</span>
              Register Item
            </button>
          </div>
        </form>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-lock me-2"></i>
                  Authentication Required
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePasswordModal}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Your security level requires authentication to create new
                  items. Please enter your encryption password to continue.
                </p>
                <div className="form-group">
                  <label htmlFor="modalPassword" className="form-label">
                    Encryption Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="modalPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your encryption password"
                    onKeyPress={(e) =>
                      e.key === "Enter" && authenticateAndRetry()
                    }
                    autoFocus
                  />
                </div>
                {error && (
                  <div className="alert alert-danger mt-2">{error}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closePasswordModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={authenticateAndRetry}
                  disabled={!password.trim()}
                >
                  <i className="fas fa-unlock me-2"></i>
                  Authenticate & Create Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPasswordModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default CreateAsset;
